'use client';

import * as React from 'react';
import { cn } from '../lib/utils.js';

/**
 * Format a number in paise to Indian rupee display string.
 * 1,23,456.78 grouping (Indian numbering system).
 */
function formatIndianRupees(paise: number): string {
  const rupees = Math.abs(paise) / 100;
  const [integerPart, decimalPart] = rupees.toFixed(2).split('.');

  // Indian grouping: last 3 digits, then groups of 2
  if (!integerPart || integerPart.length <= 3) {
    return `${paise < 0 ? '-' : ''}${integerPart}.${decimalPart}`;
  }

  const lastThree = integerPart.slice(-3);
  const rest = integerPart.slice(0, -3);
  const grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');

  return `${paise < 0 ? '-' : ''}${grouped},${lastThree}.${decimalPart}`;
}

export interface MoneyInputProps {
  value: number; // in paise
  onChange: (paise: number) => void;
  currency?: 'INR';
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  name?: string;
  placeholder?: string;
}

const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      value,
      onChange,
      currency: _currency = 'INR',
      disabled = false,
      error = false,
      className,
      id,
      name,
      placeholder = '0.00',
    },
    ref,
  ) => {
    const [displayValue, setDisplayValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);

    // Sync display value from prop when not focused
    React.useEffect(() => {
      if (!isFocused) {
        if (value === 0) {
          setDisplayValue('');
        } else {
          setDisplayValue(formatIndianRupees(value));
        }
      }
    }, [value, isFocused]);

    const handleFocus = () => {
      setIsFocused(true);
      // Show raw number on focus for editing
      if (value === 0) {
        setDisplayValue('');
      } else {
        setDisplayValue((value / 100).toFixed(2));
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      // Parse and emit final value
      const cleaned = displayValue.replace(/[^0-9.\-]/g, '');
      if (cleaned === '' || cleaned === '-') {
        onChange(0);
        setDisplayValue('');
        return;
      }
      const parsed = parseFloat(cleaned);
      if (isNaN(parsed)) {
        onChange(0);
        setDisplayValue('');
        return;
      }
      // Round to 2 decimal places then convert to paise
      const rounded = Math.round(parsed * 100);
      onChange(rounded);
      if (rounded === 0) {
        setDisplayValue('');
      } else {
        setDisplayValue(formatIndianRupees(rounded));
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      // Allow only digits, one decimal point, and leading minus
      if (raw !== '' && !/^-?\d*\.?\d{0,2}$/.test(raw)) {
        return;
      }

      setDisplayValue(raw);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Prevent non-numeric keys (except control keys)
      const allowed = [
        'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight',
        'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter',
      ];
      if (allowed.includes(e.key)) return;
      if (e.key === '-' && displayValue === '') return;
      if (e.key === '.' && !displayValue.includes('.')) return;
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <div className={cn('relative', className)}>
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
          Rs.
        </span>
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            'flex h-9 w-full rounded-md border bg-white pl-10 pr-3 py-1 text-sm text-right font-mono shadow-sm transition-colors placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
            error
              ? 'border-danger focus-visible:ring-danger'
              : 'border-gray-300',
          )}
        />
      </div>
    );
  },
);
MoneyInput.displayName = 'MoneyInput';

export { MoneyInput, formatIndianRupees };
